import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../store/auth";
import { useLangStore } from "../store/lang";
import { t } from "../i18n/strings";
import { LanguagePicker } from "../components/LanguagePicker";
import { api, ApiError } from "../lib/api";
import { trackFunnel } from "../lib/funnel";

interface RegisterResponse {
  access_token: string;
  refresh_token: string;
  user: { id: string; email: string; name: string };
  tenant: { id: string; name: string };
}

export function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantName, setTenantName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const setSession = useAuthStore((s) => s.setSession);
  const { lang } = useLangStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Capture marketing intent from the pricing page. Survives the onboarding
  // detour via localStorage so the welcome → tax flow can finish before we
  // jump them to checkout.
  useEffect(() => {
    const plan = params.get("plan");
    const cycle = params.get("cycle");
    const coupon = params.get("coupon");
    if (plan || cycle || coupon) {
      const intent = { plan, cycle, coupon, capturedAt: Date.now() };
      try {
        localStorage.setItem("clearbooks.signupIntent", JSON.stringify(intent));
      } catch {
        // localStorage disabled (private mode etc) — silently degrade.
      }
    }
    trackFunnel("register_view", { plan, cycle, coupon });
  }, [params]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Carry the A/B variant the visitor was on into the tenant record so
      // admin-side conversion analysis works long after the funnel-event
      // window has rolled over.
      let acquisition: { experiment_key?: string; variant?: string } = {};
      try {
        const raw = localStorage.getItem("clearbooks.activeVariant");
        if (raw) acquisition = JSON.parse(raw);
      } catch { /* private mode etc */ }

      const res = await api.post<RegisterResponse>("/auth/register", {
        email,
        password,
        name,
        tenantName,
        preferredLanguage: lang,
        ...(acquisition.experiment_key ? { acquisitionExperiment: acquisition.experiment_key } : {}),
        ...(acquisition.variant ? { acquisitionVariant: acquisition.variant } : {}),
      });
      setSession({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        user: res.user,
        tenant: { id: res.tenant.id, name: res.tenant.name, role: "admin" },
      });
      trackFunnel("register_submit", { tenant_id: res.tenant.id });
      navigate("/onboarding/welcome", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("An account with this email already exists.");
      } else if (err instanceof ApiError && err.status === 400) {
        setError("Check your details — password must be at least 10 characters.");
      } else {
        setError("Something went wrong. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <div>
        <div className="auth-card">
          <div className="auth-brand">
            <div className="brand-mark">C</div>
            <span className="name">ClearBooks</span>
          </div>
          <h1 className="auth-headline">
            Books, <em>ready.</em>
          </h1>
          <p className="auth-sub">Five short steps and you're sending your first invoice.</p>

          <form onSubmit={onSubmit} className="auth-fields">
            <div className="form-field">
              <label className="form-label">{t("auth.name", lang)}</label>
              <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div className="form-field">
              <label className="form-label">{t("auth.businessName", lang)}</label>
              <input
                className="form-input"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                required
              />
            </div>
            <div className="form-field">
              <label className="form-label">{t("auth.email", lang)}</label>
              <input
                className="form-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="form-field">
              <label className="form-label">{t("auth.password", lang)}</label>
              <input
                className="form-input"
                type="password"
                autoComplete="new-password"
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div className="form-help">At least 10 characters.</div>
            </div>
            {error && <div className="banner banner-error">{error}</div>}
            <div className="auth-actions">
              <button type="submit" className="btn btn-primary" disabled={submitting} style={{ justifyContent: "center" }}>
                {submitting ? t("common.loading", lang) : t("auth.signUp", lang)}
              </button>
            </div>
          </form>

          <div className="auth-foot">
            {t("auth.haveAccount", lang)} <Link to="/login">{t("auth.signIn", lang)}</Link>
          </div>
        </div>
        <div className="auth-lang">
          <LanguagePicker />
        </div>
      </div>
    </div>
  );
}
